/**
 * @fileoverview Slug → human label maps for the Discover active-filter chip
 * row. Centralised so every filter facet (`date`, `category`,
 * `neighborhood`, `price`, `q`) renders a stable, human-readable chip label
 * instead of the raw URL token.
 *
 * Prior to WEB-275, the chip row composed labels by simple
 * `${facetTitle}: ${rawSlug}` interpolation, then upper-cased the result via
 * Tailwind's `uppercase` utility. The result was strings like
 * `DATE: NEXT7 ×` — the raw URL token leaked through and the styling made
 * even known slugs unreadable (`AM` vs. `am`, etc.).
 *
 * This module owns the canonical mapping. Other surfaces (filter rail,
 * facet checklist, mobile drawer, map legend) keep their own local copies
 * where slug → label is part of the picker contract; WEB-275 intentionally
 * stays additive and does not refactor those.
 *
 * Authoritative sources reused here:
 *  - `titleCaseKebabSlug` from `apps/web/lib/title-case-kebab-slug.ts` — same
 *    helper `DiscoverNeighborhoodFilter` uses to render neighborhood rows.
 *  - The "NoDa" override mirrors `NEIGHBORHOOD_DISPLAY_OVERRIDES` inside
 *    `DiscoverNeighborhoodFilter.tsx`. Kept in sync by convention because
 *    the override set is intentionally tiny (one entry today).
 *
 * @since WEB-275
 */
import { CATEGORY_LABELS, type CategorySlug } from '@/config/categories';
import { categoryDisplayLabel } from '@/lib/city-home/categoryLabelCase';
import { PRICE_PRESET_LABELS, isPricePreset } from '@/lib/discover/pricePreset';
import { titleCaseKebabSlug } from '@/lib/title-case-kebab-slug';

/**
 * Human labels for the five `date` URL presets. Pass-through for ISO date
 * ranges (`YYYY-MM-DD..YYYY-MM-DD`) so the calendar picker's output renders
 * as-is — the picker already emits a readable range.
 */
export const DATE_PRESET_LABELS = {
  tonight: 'Tonight',
  tomorrow: 'Tomorrow',
  weekend: 'This weekend',
  next7: 'Next 7 days',
  next30: 'Next 30 days',
  'this-week': 'This week',
} as const satisfies Record<string, string>;

export type DatePresetKey = keyof typeof DATE_PRESET_LABELS;

/**
 * Human labels for the canonical ten categories, re-exported from
 * `@/config/categories` (WEB-606). Previously a hand-maintained copy of the
 * six, which is how the active-filter row could render `Category: wellness`
 * as a raw slug while the chip that set it read "Wellness".
 */
export { CATEGORY_LABELS };
export type { CategorySlug };

/**
 * Custom neighborhood display labels that don't title-case cleanly. Mirrors
 * `NEIGHBORHOOD_DISPLAY_OVERRIDES` in `DiscoverNeighborhoodFilter.tsx`.
 * Keyed by the lowercase slug.
 */
const NEIGHBORHOOD_OVERRIDES: Readonly<Record<string, string>> = {
  noda: 'NoDa',
};

function isDatePresetKey(value: string): value is DatePresetKey {
  return Object.prototype.hasOwnProperty.call(DATE_PRESET_LABELS, value);
}

function isCategorySlug(value: string): value is CategorySlug {
  return Object.prototype.hasOwnProperty.call(CATEGORY_LABELS, value);
}

/**
 * Render the human label for a `date` URL value.
 *
 * Returns `Date: <preset>` for the five canonical presets, `Date: <range>`
 * for ISO date ranges (e.g. `2026-06-01..2026-06-07`), and `Date: <raw>` for
 * anything else (defensive — `parseDiscoverParams` drops bad values).
 */
export function formatDateLabel(value: string): string {
  if (isDatePresetKey(value)) {
    return `Date: ${DATE_PRESET_LABELS[value]}`;
  }
  return `Date: ${value}`;
}

/**
 * Render the human label for a `category` URL value. Per WEB-275, category
 * chips render the bare category name (`Music`) — no `Category:` prefix.
 * Unknown slugs fall back to a title-cased rendering of the slug itself so
 * a future ingestor-added category doesn't surface as a raw kebab token.
 */
export function formatCategoryLabel(slug: string): string {
  if (isCategorySlug(slug)) {
    return categoryDisplayLabel(CATEGORY_LABELS[slug]);
  }
  return categoryDisplayLabel(titleCaseKebabSlug(slug));
}

/**
 * Render the human label for a `neighborhood` URL value.
 *
 * Prefers the lowercase override map (currently just `noda → NoDa`), then
 * falls back to `titleCaseKebabSlug` which handles standard kebab-case and
 * already-cased inputs idempotently.
 *
 * Slug-only fallback: when the chip is rendered without the facet response
 * context, this stays a TitleCase transform of the slug. A future ingestor
 * could surface a friendly name; for now the slug is the source.
 */

export function formatNeighborhoodLabel(slug: string): string {
  const trimmed = slug.trim();
  if (trimmed.length === 0) return trimmed;
  const override = NEIGHBORHOOD_OVERRIDES[trimmed.toLowerCase()];
  if (override) return override;
  return titleCaseKebabSlug(trimmed);
}

/**
 * Render the human label for a `price` URL value (WEB-656 / DPL-06 canonical
 * presets: `free`, `0-25`, `25-plus`). Falls back to the raw value with a
 * prefix for anything else — defensive only, `parseDiscoverParams` already
 * normalizes/drops non-canonical values before they reach this formatter.
 */
export function formatPriceLabel(value: string): string {
  if (isPricePreset(value)) {
    return `Price: ${PRICE_PRESET_LABELS[value]}`;
  }
  return `Price: ${value}`;
}

/**
 * Render the human label for a `q` (free-text search) URL value.
 */
export function formatSearchLabel(value: string): string {
  return `Search: "${value}"`;
}
